import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { ANNUAL_CROSS_TOPIC_DETAIL_CAPTURE_CSS } from './annualCrossTopicDetailCaptureCss';
import {
  DOCUMENT_EXPORT_INLINE_STYLE_OPTIONS,
  DOCUMENT_EXPORT_META_INLINE_STYLE_OPTIONS,
  inlineVisualStylesFromSource,
} from './captureInlineVisualStyles';
import {
  DOCUMENT_EXPORT_CAPTURE_SCALES,
  DOCUMENT_EXPORT_CAPTURE_SETTLE_MS,
  createDocumentExportCaptureShell,
  createDocumentExportOnclone,
  createShellCaptureOnclone,
  type DocumentExportCloneCustomizer,
} from './documentExportLightTheme';
import {
  boostMetaCanvasForRender,
  DOCUMENT_EXPORT_META_RENDER_SCALE,
  prepareExportMetaCaptureClone,
  resolveExportMetaCaptureWidthPx,
  resolveMetaNormalizeMaxUpscale,
  WEEKLY_GUIDE_META_RENDER_SCALE,
} from './documentExportMetaTitleFonts';

export type BlockContentExportHooks = {
  /** html2canvas 캡처 직전(라이브 DOM) */
  beforeCapture?: () => void;
  /** 본문 table-wrap 클론 직후 */
  prepareBodyClone?: (clone: HTMLElement, source: HTMLElement) => void;
  /** tbody 분할 캡처용 클론 직후 */
  prepareTableSectionClone?: (clone: HTMLElement, liveSections: HTMLElement[]) => void;
  /** html2canvas iframe 클론 추가 보정 */
  customizeCaptureClone?: DocumentExportCloneCustomizer;
  /** 라이브 화면 DOM + 캡처 노드 기준 onclone 커스터마이저 */
  createCaptureCloneCustomizer?: (ctx: {
    liveSource: HTMLElement;
    captureRoot: HTMLElement;
  }) => DocumentExportCloneCustomizer;
  /** true면 tbody 분할·이음 캡처 폴백 생략(주간학습 안내 등) */
  disableSegmentedTableFallback?: boolean;
  /** 캡처 최소 너비(px). 미지정 시 920 */
  captureMinWidthPx?: number;
  /** 본문 클론 inlineVisualStyles 옵션 — 미지정 시 DOCUMENT_EXPORT_INLINE_STYLE_OPTIONS */
  bodyInlineStyleOptions?: typeof DOCUMENT_EXPORT_INLINE_STYLE_OPTIONS;
};

const DEFAULT_CAPTURE_CSS = ANNUAL_CROSS_TOPIC_DETAIL_CAPTURE_CSS;

const PDF_MARGIN_MM = 10;
const PDF_BLOCK_GAP_MM = 3;
/** PDF 본문 상단 — 제목 위 여백 */
const PDF_META_TOP_GAP_MM = 6;
const CAPTURE_SETTLE_MS = DOCUMENT_EXPORT_CAPTURE_SETTLE_MS;

const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;

/** 브라우저 머릿글과 본문 제목 사이 여백 */
const PRINT_PAGE_MARGIN_TOP_MM = 18;
const PRINT_PAGE_MARGIN_OTHER_MM = 10;
const PRINT_BODY_PADDING_TOP_MM = 5;
/** Chrome/Safari 인쇄 머릿글(날짜·문서제목)이 차지하는 대략 높이 */
const PRINT_BROWSER_HEADER_ALLOWANCE_MM = 14;
/** 인쇄 HTML에서 이미지 스택 간격(대략) */
const PRINT_IMG_STACK_GAP_MM = 1.5;
/** 본문 행을 넣기에 부족하면 제목·헤더만 두고 다음 페이지로 넘김 */
const MIN_TABLE_BODY_SLICE_MM = 10;

function buildPrintPageCss(): string {
  return `
    @page {
      size: A4 portrait;
      margin: ${PRINT_PAGE_MARGIN_TOP_MM}mm ${PRINT_PAGE_MARGIN_OTHER_MM}mm ${PRINT_PAGE_MARGIN_OTHER_MM}mm ${PRINT_PAGE_MARGIN_OTHER_MM}mm;
    }
    body {
      margin: 0;
      padding: ${PRINT_BODY_PADDING_TOP_MM}mm 0 0 0;
      background: #fff;
      box-sizing: border-box;
    }
  `;
}

function buildPrintTablePageCss(): string {
  return `
    ${buildPrintPageCss()}
    .cp-modal-print-page {
      page-break-inside: avoid;
      break-inside: avoid;
    }
    .cp-modal-print-page img {
      width: 100%;
      height: auto;
      display: block;
      margin: 0 0 ${PRINT_IMG_STACK_GAP_MM}mm 0;
    }
    .cp-modal-print-page img:last-child {
      margin-bottom: 0;
    }
    .cp-modal-print-page-break {
      page-break-before: always;
      break-before: page;
    }
  `;
}

function resolvePrintContentWidthMm(): number {
  return A4_WIDTH_MM - PRINT_PAGE_MARGIN_OTHER_MM * 2;
}

/** 인쇄 중 브라우저 탭·머릿글에 표시할 임시 문서 제목 */
const BROWSER_PRINT_DOCUMENT_TITLE = 'PRINT';

type TableExportArtifacts = {
  metaCanvas: HTMLCanvasElement | null;
  theadCanvas: HTMLCanvasElement;
  tbodyCanvas: HTMLCanvasElement;
};

type BlockExportArtifacts = {
  metaCanvas: HTMLCanvasElement | null;
  bodyCanvas: HTMLCanvasElement;
};

export type { BlockExportArtifacts };

function buildPrintFitScaledCss(bodyScale: number): string {
  const innerWidthPercent = bodyScale > 0 ? 100 / bodyScale : 100;
  return `
    .cp-modal-print-fit-outer {
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-start;
      box-sizing: border-box;
    }
    .cp-modal-print-fit-meta {
      width: 100%;
      max-width: 100%;
      overflow: visible;
      box-sizing: border-box;
      margin-top: 4mm;
      margin-bottom: 2mm;
    }
    .cp-modal-print-fit-meta img {
      width: 100%;
      max-width: 100%;
      height: auto;
      display: block;
      margin: 0 0 3px 0;
      box-sizing: border-box;
    }
    .cp-modal-print-fit-body-scaled {
      transform: scale(${bodyScale});
      transform-origin: top center;
      width: ${innerWidthPercent}%;
      max-width: ${innerWidthPercent}%;
    }
    .cp-modal-print-fit-body-scaled img {
      width: 100%;
      height: auto;
      display: block;
      margin: 0;
    }
  `;
}

/**
 * 새 탭(about:blank) 대신 숨김 iframe에서 인쇄 — 머릿글 URL에 about:blank가 찍히지 않고,
 * 인쇄 대화상자 종료 후 정리됩니다.
 */
function printHtmlDocument(html: string): void {
  const previousTitle = document.title;
  document.title = BROWSER_PRINT_DOCUMENT_TITLE;

  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.cssText =
    'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;pointer-events:none;';
  document.body.appendChild(iframe);

  const printWindow = iframe.contentWindow;
  if (!printWindow) {
    document.title = previousTitle;
    iframe.remove();
    alert('프린트를 시작할 수 없습니다.');
    return;
  }

  let cleanedUp = false;
  const restoreTitle = () => {
    document.title = previousTitle;
  };
  const cleanup = () => {
    if (cleanedUp) return;
    cleanedUp = true;
    restoreTitle();
    iframe.remove();
  };

  const triggerPrint = () => {
    printWindow.focus();
    printWindow.print();
    printWindow.addEventListener('afterprint', cleanup, { once: true });
    window.addEventListener('afterprint', cleanup, { once: true });
    window.setTimeout(cleanup, 60_000);
  };

  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();

  window.setTimeout(triggerPrint, 300);
}

/** 전역 `@media print` 간섭 없이 독립 HTML을 iframe 인쇄 */
export function printStandaloneHtmlDocument(html: string): void {
  printHtmlDocument(html);
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function canvasHeightMm(canvas: HTMLCanvasElement, widthMm: number): number {
  if (canvas.width <= 0) return 0;
  return (canvas.height * widthMm) / canvas.width;
}

function prepareMetaForOutput(
  metaCanvas: HTMLCanvasElement | null,
  contentW: number,
  renderScale = DOCUMENT_EXPORT_META_RENDER_SCALE
): { canvas: HTMLCanvasElement | null; dataUrl: string | null; heightMm: number } {
  const canvas = boostMetaCanvasForRender(metaCanvas, renderScale);
  return {
    canvas,
    dataUrl: canvas?.toDataURL('image/png') ?? null,
    heightMm: canvas ? canvasHeightMm(canvas, contentW) : 0,
  };
}

function sliceCanvasVertical(source: HTMLCanvasElement, topPx: number, heightPx: number): HTMLCanvasElement {
  const slice = document.createElement('canvas');
  slice.width = source.width;
  slice.height = Math.max(1, heightPx);
  const ctx = slice.getContext('2d');
  if (!ctx) return slice;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, slice.width, slice.height);
  ctx.drawImage(source, 0, topPx, source.width, heightPx, 0, 0, source.width, heightPx);
  return slice;
}

function buildFullTableCaptureNode(
  table: HTMLTableElement,
  useLiveComputedStyles: boolean,
  captureWidthPx: number
): HTMLElement {
  const wrap = document.createElement('div');
  wrap.style.width = `${captureWidthPx}px`;
  wrap.style.overflow = 'visible';
  wrap.style.background = '#ffffff';
  const clone = table.cloneNode(true) as HTMLTableElement;
  clone.style.width = `${captureWidthPx}px`;
  clone.style.minWidth = `${captureWidthPx}px`;
  if (useLiveComputedStyles) {
    inlineVisualStylesFromSource(table, clone, DOCUMENT_EXPORT_INLINE_STYLE_OPTIONS);
  }
  wrap.appendChild(clone);
  return wrap;
}

function measureTheadHeightRatio(table: HTMLTableElement): number {
  const thead = table.querySelector('thead') as HTMLElement | null;
  if (!thead || table.offsetHeight <= 0) return 0.08;
  return Math.min(0.4, Math.max(0.04, thead.offsetHeight / table.offsetHeight));
}

function resolveCaptureTableWidthPx(table: HTMLTableElement): number {
  return Math.max(table.offsetWidth, table.scrollWidth, 900);
}

const INLINE_EXPORT_STYLE_OPTIONS = DOCUMENT_EXPORT_INLINE_STYLE_OPTIONS;

function pickCaptureCloneHooks(
  hooks?: BlockContentExportHooks
): Pick<BlockContentExportHooks, 'customizeCaptureClone' | 'createCaptureCloneCustomizer'> | undefined {
  if (!hooks?.customizeCaptureClone && !hooks?.createCaptureCloneCustomizer) {
    return undefined;
  }
  return {
    customizeCaptureClone: hooks.customizeCaptureClone,
    createCaptureCloneCustomizer: hooks.createCaptureCloneCustomizer,
  };
}

function createCaptureShell(widthPx: number, captureCss: string = DEFAULT_CAPTURE_CSS): HTMLDivElement {
  return createDocumentExportCaptureShell(widthPx, captureCss);
}

type CaptureStyleOptions = {
  styleSource?: HTMLElement;
  styleTargetSelector?: string;
  /** 화면에 보이는 원본 — onclone 색상 복사용 */
  liveColorSource?: HTMLElement;
  inlineStyleOptions?: typeof DOCUMENT_EXPORT_INLINE_STYLE_OPTIONS;
};

export async function captureNodeInDocumentExportShell(
  shell: HTMLDivElement,
  node: HTMLElement,
  styleOptions?: CaptureStyleOptions,
  slotWidthPx?: number,
  captureCss?: string,
  captureHooks?: Pick<BlockContentExportHooks, 'customizeCaptureClone' | 'createCaptureCloneCustomizer'>
): Promise<HTMLCanvasElement> {
  const isMetaHeader = node.matches('[data-pdf-element="header"]');
  const metaCaptureWidthPx = isMetaHeader ? resolveExportMetaCaptureWidthPx(node, slotWidthPx) : null;
  const effectiveSlotWidthPx = metaCaptureWidthPx ?? slotWidthPx;

  const slot = document.createElement('div');
  slot.style.width = effectiveSlotWidthPx ? `${effectiveSlotWidthPx}px` : '100%';
  slot.style.background = '#ffffff';
  slot.style.overflow = 'visible';
  const clone = node.cloneNode(true) as HTMLElement;
  // 메타 제목: 화면 computed style 복사 시 font-size·white-space 등이 캡처 설정을 덮어씀
  if (styleOptions?.styleSource && !isMetaHeader) {
    const styleTarget = styleOptions.styleTargetSelector
      ? (clone.querySelector(styleOptions.styleTargetSelector) as HTMLElement | null)
      : clone;
    if (styleTarget) {
      inlineVisualStylesFromSource(styleOptions.styleSource, styleTarget, {
        ...DOCUMENT_EXPORT_INLINE_STYLE_OPTIONS,
        ...styleOptions.inlineStyleOptions,
      });
    }
  }
  if (isMetaHeader && metaCaptureWidthPx) {
    prepareExportMetaCaptureClone(clone, metaCaptureWidthPx);
  }
  slot.appendChild(clone);
  shell.appendChild(slot);

  const html2canvasOptions = {
    useCORS: true,
    logging: false,
    backgroundColor: '#ffffff',
  };

  try {
    await new Promise((resolve) => setTimeout(resolve, CAPTURE_SETTLE_MS));
    for (const scale of DOCUMENT_EXPORT_CAPTURE_SCALES) {
      try {
        const canvas = await html2canvas(slot, {
          ...html2canvasOptions,
          scale,
          onclone: createDocumentExportOnclone(
            (clonedDoc, clonedElement) => {
              const captureCloneCustomizer = captureHooks?.createCaptureCloneCustomizer?.({
                liveSource: styleOptions?.liveColorSource ?? styleOptions?.styleSource ?? node,
                captureRoot: node,
              });
              if (captureCloneCustomizer) {
                captureCloneCustomizer(clonedDoc, clonedElement);
              } else {
                createShellCaptureOnclone(captureCss)(clonedDoc, clonedElement);
                captureHooks?.customizeCaptureClone?.(clonedDoc, clonedElement);
              }
            },
            { moveCloneOffScreen: false }
          ),
        });
        if (canvas.height >= 2 && canvas.width >= 2) {
          return canvas;
        }
      } catch (error) {
        console.warn('[document-export] html2canvas capture failed:', error);
      }
    }
    throw new Error('캡처 이미지 생성에 실패했습니다.');
  } finally {
    shell.removeChild(slot);
  }
}

function normalizeCanvasToWidth(source: HTMLCanvasElement, targetWidthPx: number): HTMLCanvasElement {
  if (source.width === targetWidthPx) return source;
  const normalized = document.createElement('canvas');
  normalized.width = targetWidthPx;
  normalized.height = Math.max(1, Math.round((source.height * targetWidthPx) / source.width));
  const ctx = normalized.getContext('2d');
  if (!ctx) return source;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, normalized.width, normalized.height);
  ctx.drawImage(source, 0, 0, targetWidthPx, normalized.height);
  return normalized;
}

/** 메타 제목 normalize — 세로 확대 상한을 두어 과대·가로 잘림 방지 */
function normalizeMetaCanvasToBodyWidth(
  source: HTMLCanvasElement,
  targetWidthPx: number,
  maxUpscale: number
): HTMLCanvasElement {
  if (source.width <= 0 || targetWidthPx <= 0) return source;
  if (source.width === targetWidthPx) return source;

  const upscale = targetWidthPx / source.width;
  if (upscale <= maxUpscale) {
    return normalizeCanvasToWidth(source, targetWidthPx);
  }

  const cappedWidth = Math.max(1, Math.round(source.width * maxUpscale));
  const scaled = normalizeCanvasToWidth(source, cappedWidth);
  if (scaled.width >= targetWidthPx) return scaled;

  const padded = document.createElement('canvas');
  padded.width = targetWidthPx;
  padded.height = scaled.height;
  const ctx = padded.getContext('2d');
  if (!ctx) return scaled;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, padded.width, padded.height);
  const offsetX = Math.floor((targetWidthPx - scaled.width) / 2);
  ctx.drawImage(scaled, offsetX, 0);
  return padded;
}

function splitFullTableCanvas(
  fullCanvas: HTMLCanvasElement,
  theadHeightRatio: number
): { theadCanvas: HTMLCanvasElement; tbodyCanvas: HTMLCanvasElement } {
  const theadSlicePx = Math.max(1, Math.min(fullCanvas.height - 1, Math.round(fullCanvas.height * theadHeightRatio)));
  const tbodySlicePx = fullCanvas.height - theadSlicePx;
  return {
    theadCanvas: sliceCanvasVertical(fullCanvas, 0, theadSlicePx),
    tbodyCanvas: sliceCanvasVertical(fullCanvas, theadSlicePx, tbodySlicePx),
  };
}

function stitchCanvasesVertically(canvases: HTMLCanvasElement[]): HTMLCanvasElement {
  if (!canvases.length) {
    throw new Error('캡처 이미지 조합에 실패했습니다.');
  }
  if (canvases.length === 1) return canvases[0]!;

  const width = Math.max(...canvases.map((canvas) => canvas.width));
  const totalHeight = canvases.reduce((sum, canvas) => sum + canvas.height, 0);
  const stitched = document.createElement('canvas');
  stitched.width = width;
  stitched.height = Math.max(1, totalHeight);
  const ctx = stitched.getContext('2d');
  if (!ctx) return canvases[0]!;

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, stitched.width, stitched.height);

  let y = 0;
  canvases.forEach((canvas) => {
    const normalized = normalizeCanvasToWidth(canvas, width);
    ctx.drawImage(normalized, 0, y);
    y += normalized.height;
  });

  return stitched;
}

async function captureTableSectionsInShell(
  shell: HTMLDivElement,
  liveTable: HTMLTableElement,
  liveSections: HTMLElement[],
  captureWidthPx: number,
  useLiveComputedStyles: boolean,
  captureCss: string,
  hooks?: BlockContentExportHooks
): Promise<HTMLCanvasElement> {
  const wrap = document.createElement('div');
  wrap.style.width = `${captureWidthPx}px`;
  wrap.style.overflow = 'visible';
  wrap.style.background = '#ffffff';

  const table = document.createElement('table');
  table.className = liveTable.className;
  table.style.width = `${captureWidthPx}px`;
  table.style.minWidth = `${captureWidthPx}px`;

  const clonedSections: HTMLElement[] = [];
  liveSections.forEach((section) => {
    const clone = section.cloneNode(true) as HTMLElement;
    clonedSections.push(clone);
    table.appendChild(clone);
  });

  wrap.appendChild(table);
  if (useLiveComputedStyles) {
    for (let index = 0; index < liveSections.length; index += 1) {
      const liveSection = liveSections[index];
      const capSection = clonedSections[index];
      if (!liveSection || !capSection) continue;
      inlineVisualStylesFromSource(liveSection, capSection, INLINE_EXPORT_STYLE_OPTIONS);
    }
  }

  hooks?.prepareTableSectionClone?.(wrap, liveSections);

  return captureNodeInDocumentExportShell(
    shell,
    wrap,
    undefined,
    captureWidthPx,
    captureCss,
    pickCaptureCloneHooks(hooks)
  );
}

async function buildSegmentedTableBodyCanvas(
  bodyEl: HTMLElement,
  shell: HTMLDivElement,
  captureWidthPx: number,
  useLiveComputedStyles: boolean,
  captureCss: string,
  hooks?: BlockContentExportHooks
): Promise<HTMLCanvasElement> {
  const liveTable = bodyEl.querySelector('table') as HTMLTableElement | null;
  if (!liveTable) {
    throw new Error('보낼 테이블 데이터가 없습니다.');
  }

  const thead = liveTable.querySelector('thead') as HTMLElement | null;
  const tbodies = Array.from(liveTable.querySelectorAll('tbody')) as HTMLElement[];
  if (!thead || !tbodies.length) {
    throw new Error('보낼 테이블 데이터가 없습니다.');
  }

  const segmentCanvases: HTMLCanvasElement[] = [];
  segmentCanvases.push(
    await captureTableSectionsInShell(
      shell,
      liveTable,
      [thead, tbodies[0]!],
      captureWidthPx,
      useLiveComputedStyles,
      captureCss,
      hooks
    )
  );

  for (let index = 1; index < tbodies.length; index += 1) {
    segmentCanvases.push(
      await captureTableSectionsInShell(
        shell,
        liveTable,
        [tbodies[index]!],
        captureWidthPx,
        useLiveComputedStyles,
        captureCss,
        hooks
      )
    );
  }

  return stitchCanvasesVertically(segmentCanvases);
}

export async function buildBlockExportArtifacts(
  root: HTMLElement,
  metaHeaderSelector: string,
  bodySelector: string,
  captureCss: string = DEFAULT_CAPTURE_CSS,
  useLiveComputedStyles = true,
  hooks?: BlockContentExportHooks
): Promise<BlockExportArtifacts> {
  const bodyEl = root.querySelector(bodySelector) as HTMLElement | null;
  if (!bodyEl) {
    throw new Error('보낼 본문 데이터가 없습니다.');
  }

  hooks?.beforeCapture?.();

  const captureMinWidth = hooks?.captureMinWidthPx ?? 920;
  const captureWidthPx = Math.max(bodyEl.offsetWidth, bodyEl.scrollWidth, captureMinWidth);
  const shell = createCaptureShell(captureWidthPx, captureCss);

  try {
    const bodyWrap = document.createElement('div');
    bodyWrap.style.width = `${captureWidthPx}px`;
    bodyWrap.style.background = '#ffffff';
    const bodyClone = bodyEl.cloneNode(true) as HTMLElement;
    bodyClone.style.overflow = 'visible';
    if (useLiveComputedStyles) {
      inlineVisualStylesFromSource(
        bodyEl,
        bodyClone,
        hooks?.bodyInlineStyleOptions ?? INLINE_EXPORT_STYLE_OPTIONS
      );
    }
    hooks?.prepareBodyClone?.(bodyClone, bodyEl);
    bodyWrap.appendChild(bodyClone);

    let bodyCanvas: HTMLCanvasElement;
    try {
      bodyCanvas = await captureNodeInDocumentExportShell(
        shell,
        bodyWrap,
        { liveColorSource: bodyEl },
        captureWidthPx,
        captureCss,
        pickCaptureCloneHooks(hooks)
      );
    } catch (captureError) {
      if (hooks?.disableSegmentedTableFallback || !bodyEl.querySelector('table')) {
        throw captureError instanceof Error ? captureError : new Error('본문 캡처에 실패했습니다.');
      }
      bodyCanvas = await buildSegmentedTableBodyCanvas(
        bodyEl,
        shell,
        captureWidthPx,
        useLiveComputedStyles,
        captureCss,
        hooks
      );
    }

    if (
      !hooks?.disableSegmentedTableFallback &&
      (bodyCanvas.height < 2 || bodyCanvas.width < 2) &&
      bodyEl.querySelector('table')
    ) {
      bodyCanvas = await buildSegmentedTableBodyCanvas(
        bodyEl,
        shell,
        captureWidthPx,
        useLiveComputedStyles,
        captureCss,
        hooks
      );
    }

    if (bodyCanvas.height < 2 || bodyCanvas.width < 2) {
      throw new Error('본문 캡처에 실패했습니다.');
    }

    const metaEl = root.querySelector(metaHeaderSelector) as HTMLElement | null;
    const metaCanvas = metaEl
      ? await captureNodeInDocumentExportShell(
          shell,
          metaEl,
          useLiveComputedStyles
            ? {
                styleSource: metaEl,
                liveColorSource: metaEl,
                inlineStyleOptions: DOCUMENT_EXPORT_META_INLINE_STYLE_OPTIONS,
              }
            : { liveColorSource: metaEl },
          bodyCanvas.width,
          captureCss,
          pickCaptureCloneHooks(hooks)
        )
      : null;

    return {
      metaCanvas: metaCanvas
        ? normalizeMetaCanvasToBodyWidth(
            metaCanvas,
            bodyCanvas.width,
            resolveMetaNormalizeMaxUpscale(metaEl)
          )
        : null,
      bodyCanvas,
    };
  } finally {
    if (shell.parentNode) {
      document.body.removeChild(shell);
    }
  }
}

export type BlockArtifactRenderOptions = {
  /** 인쇄 가능 영역 높이 대비 목표 비율(예: 2/3) — 제목+본문을 한 페이지에 비율 맞춤 */
  fitPageHeightRatio?: number;
};

function pdfMetaTopGapMm(hasMeta: boolean): number {
  return hasMeta ? PDF_META_TOP_GAP_MM : 0;
}

function renderBlockArtifactsFitSinglePage(
  pdf: jsPDF,
  artifacts: BlockExportArtifacts,
  contentW: number,
  printableHm: number,
  fitPageHeightRatio: number
): void {
  const metaOutput = prepareMetaForOutput(artifacts.metaCanvas, contentW, WEEKLY_GUIDE_META_RENDER_SCALE);
  const metaHm = metaOutput.heightMm;
  const bodyHm = canvasHeightMm(artifacts.bodyCanvas, contentW);
  if (bodyHm <= 0) {
    throw new Error('본문 이미지 높이 계산에 실패했습니다.');
  }

  const metaTopGap = pdfMetaTopGapMm(metaHm > 0);
  const gapHm = metaHm > 0 ? PDF_BLOCK_GAP_MM : 0;
  const targetHm = printableHm * fitPageHeightRatio;
  const bodyTargetHm = Math.max(0, targetHm - metaHm - gapHm - metaTopGap);
  const bodyScale = bodyHm > 0 ? Math.min(1, bodyTargetHm / bodyHm) : 1;
  const drawMetaHm = metaHm;
  const drawBodyHm = bodyHm * bodyScale;
  const drawBodyW = contentW * bodyScale;
  const metaX = PDF_MARGIN_MM;
  const bodyX = PDF_MARGIN_MM + (contentW - drawBodyW) / 2;
  let y = PDF_MARGIN_MM + metaTopGap;

  if (metaOutput.canvas && metaOutput.dataUrl && metaHm > 0) {
    pdf.addImage(metaOutput.dataUrl, 'PNG', metaX, y, contentW, drawMetaHm);
    y += drawMetaHm + gapHm;
  }

  pdf.addImage(artifacts.bodyCanvas.toDataURL('image/png'), 'PNG', bodyX, y, drawBodyW, drawBodyHm);
}

async function renderPdfFromBlockArtifacts(
  artifacts: BlockExportArtifacts,
  fileName: string,
  options?: BlockArtifactRenderOptions
): Promise<void> {
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pdfH = pdf.internal.pageSize.getHeight();
  const pdfW = pdf.internal.pageSize.getWidth();
  const contentW = pdfW - PDF_MARGIN_MM * 2;
  const printableHm = pdfH - PDF_MARGIN_MM * 2;

  if (options?.fitPageHeightRatio) {
    renderBlockArtifactsFitSinglePage(pdf, artifacts, contentW, printableHm, options.fitPageHeightRatio);
    pdf.save(fileName);
    return;
  }

  const metaOutput = prepareMetaForOutput(artifacts.metaCanvas, contentW);
  const metaHm = metaOutput.heightMm;
  const bodyTotalHm = canvasHeightMm(artifacts.bodyCanvas, contentW);
  if (bodyTotalHm <= 0) {
    throw new Error('본문 이미지 높이 계산에 실패했습니다.');
  }
  const metaDataUrl = metaOutput.dataUrl;

  let srcY = 0;
  let pageIndex = 0;

  while (srcY < artifacts.bodyCanvas.height) {
    if (pageIndex > 0) pdf.addPage();
    let y = PDF_MARGIN_MM;

    if (pageIndex === 0 && metaDataUrl) {
      y += pdfMetaTopGapMm(true);
      pdf.addImage(metaDataUrl, 'PNG', PDF_MARGIN_MM, y, contentW, metaHm);
      y += metaHm + PDF_BLOCK_GAP_MM;
    }

    const remainingMm = pdfH - PDF_MARGIN_MM - y;
    if (remainingMm <= 2) {
      throw new Error('인쇄 페이지 레이아웃 계산에 실패했습니다.');
    }

    const maxSlicePx = Math.min(
      artifacts.bodyCanvas.height - srcY,
      Math.max(1, Math.floor((remainingMm / bodyTotalHm) * artifacts.bodyCanvas.height))
    );

    const sliceCanvas = sliceCanvasVertical(artifacts.bodyCanvas, srcY, maxSlicePx);
    const sliceHm = canvasHeightMm(sliceCanvas, contentW);
    pdf.addImage(sliceCanvas.toDataURL('image/png'), 'PNG', PDF_MARGIN_MM, y, contentW, sliceHm);

    srcY += maxSlicePx;
    pageIndex += 1;

    if (srcY < artifacts.bodyCanvas.height && pageIndex > 500) {
      throw new Error('인쇄 페이지 분할 한도를 초과했습니다.');
    }
  }

  pdf.save(fileName);
}

async function renderPrintFromBlockArtifacts(
  artifacts: BlockExportArtifacts,
  title: string,
  options?: BlockArtifactRenderOptions
): Promise<void> {
  const pdfH = new jsPDF('p', 'mm', 'a4').internal.pageSize.getHeight();
  const pdfW = new jsPDF('p', 'mm', 'a4').internal.pageSize.getWidth();
  const contentW = pdfW - PDF_MARGIN_MM * 2;
  const printableHm = pdfH - PDF_MARGIN_MM * 2;

  if (options?.fitPageHeightRatio) {
    const metaOutput = prepareMetaForOutput(artifacts.metaCanvas, contentW, WEEKLY_GUIDE_META_RENDER_SCALE);
    const metaHm = metaOutput.heightMm;
    const bodyHm = canvasHeightMm(artifacts.bodyCanvas, contentW);
    const metaTopGap = pdfMetaTopGapMm(metaHm > 0);
    const gapHm = metaHm > 0 ? PDF_BLOCK_GAP_MM : 0;
    const targetHm = printableHm * options.fitPageHeightRatio;
    const bodyTargetHm = Math.max(0, targetHm - metaHm - gapHm - metaTopGap);
    const bodyScale = bodyHm > 0 ? Math.min(1, bodyTargetHm / bodyHm) : 1;
    const metaDataUrl = metaOutput.dataUrl;
    const bodyDataUrl = artifacts.bodyCanvas.toDataURL('image/png');

    const printHtml = `
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${BROWSER_PRINT_DOCUMENT_TITLE}</title>
          <style>
            ${buildPrintPageCss()}
            ${buildPrintFitScaledCss(bodyScale)}
          </style>
        </head>
        <body>
          <div class="cp-modal-print-fit-outer">
            ${metaDataUrl ? `<div class="cp-modal-print-fit-meta"><img src="${metaDataUrl}" alt="" /></div>` : ''}
            <div class="cp-modal-print-fit-body-scaled">
              <img src="${bodyDataUrl}" alt="" />
            </div>
          </div>
        </body>
      </html>
    `;
    printHtmlDocument(printHtml);
    return;
  }

  const metaOutput = prepareMetaForOutput(artifacts.metaCanvas, contentW);
  const metaHm = metaOutput.heightMm;
  const bodyTotalHm = canvasHeightMm(artifacts.bodyCanvas, contentW);
  if (bodyTotalHm <= 0) {
    throw new Error('본문 이미지 높이 계산에 실패했습니다.');
  }
  const metaDataUrl = metaOutput.dataUrl;

  const pages: Array<{ meta?: string; bodySlice: string }> = [];
  let srcY = 0;
  let pageIndex = 0;

  while (srcY < artifacts.bodyCanvas.height) {
    let y = PDF_MARGIN_MM;
    const includeMeta = pageIndex === 0 && metaDataUrl;
    if (includeMeta) {
      y += pdfMetaTopGapMm(true) + metaHm + PDF_BLOCK_GAP_MM;
    }

    const remainingMm = pdfH - PDF_MARGIN_MM - y;
    if (remainingMm <= 2) {
      throw new Error('인쇄 페이지 레이아웃 계산에 실패했습니다.');
    }

    const maxSlicePx = Math.min(
      artifacts.bodyCanvas.height - srcY,
      Math.max(1, Math.floor((remainingMm / bodyTotalHm) * artifacts.bodyCanvas.height))
    );

    const sliceCanvas = sliceCanvasVertical(artifacts.bodyCanvas, srcY, maxSlicePx);
    pages.push({
      meta: includeMeta ? metaDataUrl ?? undefined : undefined,
      bodySlice: sliceCanvas.toDataURL('image/png'),
    });

    srcY += maxSlicePx;
    pageIndex += 1;

    if (srcY < artifacts.bodyCanvas.height && pageIndex > 500) {
      throw new Error('인쇄 페이지 분할 한도를 초과했습니다.');
    }
  }

  const pagesHtml = pages
    .map((page, index) => {
      const imgs = [page.meta ? `<img src="${page.meta}" alt="" />` : '', `<img src="${page.bodySlice}" alt="" />`]
        .filter(Boolean)
        .join('');
      const breakClass = index > 0 ? ' cp-modal-print-page-break' : '';
      return `<div class="cp-modal-print-page${breakClass}">${imgs}</div>`;
    })
    .join('');

  const printHtml = `
    <html>
      <head>
        <meta charset="utf-8" />
        <title>${BROWSER_PRINT_DOCUMENT_TITLE}</title>
        <style>
          ${buildPrintPageCss()}
          .cp-modal-print-page img {
            width: 100%;
            height: auto;
            display: block;
            margin: 0 0 6px 0;
          }
          .cp-modal-print-page-break {
            page-break-before: always;
            break-before: page;
          }
        </style>
      </head>
      <body>
        ${pagesHtml}
      </body>
    </html>
  `;
  printHtmlDocument(printHtml);
}

async function buildSegmentedTableExportCanvases(
  liveTable: HTMLTableElement,
  shell: HTMLDivElement,
  captureWidthPx: number,
  useLiveComputedStyles: boolean,
  captureCss: string
): Promise<{ theadCanvas: HTMLCanvasElement; tbodyCanvas: HTMLCanvasElement }> {
  const thead = liveTable.querySelector('thead') as HTMLElement | null;
  const tbodies = Array.from(liveTable.querySelectorAll('tbody')) as HTMLElement[];
  if (!thead || !tbodies.length) {
    throw new Error('보낼 테이블 데이터가 없습니다.');
  }

  const theadCanvas = await captureTableSectionsInShell(
    shell,
    liveTable,
    [thead],
    captureWidthPx,
    useLiveComputedStyles,
    captureCss
  );

  const tbodyCanvases: HTMLCanvasElement[] = [];
  for (const tbody of tbodies) {
    tbodyCanvases.push(
      await captureTableSectionsInShell(shell, liveTable, [tbody], captureWidthPx, useLiveComputedStyles, captureCss)
    );
  }

  return {
    theadCanvas,
    tbodyCanvas: stitchCanvasesVertically(tbodyCanvases),
  };
}

async function buildTableExportArtifacts(
  root: HTMLElement,
  metaHeaderSelector: string,
  tableSelector: string,
  captureCss: string = DEFAULT_CAPTURE_CSS,
  useLiveComputedStyles = true
): Promise<TableExportArtifacts> {
  const table = root.querySelector(tableSelector) as HTMLTableElement | null;
  const tbody = table?.querySelector('tbody');
  if (!table || !tbody || tbody.rows.length === 0) {
    throw new Error('보낼 테이블 데이터가 없습니다.');
  }

  const captureTableWidthPx = resolveCaptureTableWidthPx(table);
  const shell = createCaptureShell(captureTableWidthPx, captureCss);

  try {
    const metaEl = root.querySelector(metaHeaderSelector) as HTMLElement | null;

    const { theadCanvas, tbodyCanvas } = await buildSegmentedTableExportCanvases(
      table,
      shell,
      captureTableWidthPx,
      useLiveComputedStyles,
      captureCss
    );

    const metaCanvas = metaEl
      ? await captureNodeInDocumentExportShell(
          shell,
          metaEl,
          useLiveComputedStyles
            ? { styleSource: metaEl, inlineStyleOptions: DOCUMENT_EXPORT_META_INLINE_STYLE_OPTIONS }
            : undefined,
          tbodyCanvas.width,
          captureCss
        )
      : null;

    if (theadCanvas.height < 2 || tbodyCanvas.height < 2) {
      throw new Error('테이블 캡처에 실패했습니다.');
    }

    return {
      metaCanvas: metaCanvas
        ? normalizeMetaCanvasToBodyWidth(
            metaCanvas,
            tbodyCanvas.width,
            resolveMetaNormalizeMaxUpscale(metaEl)
          )
        : null,
      theadCanvas: normalizeCanvasToWidth(theadCanvas, tbodyCanvas.width),
      tbodyCanvas,
    };
  } finally {
    if (shell.parentNode) {
      document.body.removeChild(shell);
    }
  }
}

type PrintPageSlice = {
  meta?: string;
  thead: string;
  bodySlice?: string;
};

function buildPrintPageSlices(artifacts: TableExportArtifacts, contentW: number): PrintPageSlice[] {
  const theadHm = canvasHeightMm(artifacts.theadCanvas, contentW);
  const metaOutput = prepareMetaForOutput(artifacts.metaCanvas, contentW);
  const metaHm = metaOutput.heightMm;
  const tbodyTotalHm = canvasHeightMm(artifacts.tbodyCanvas, contentW);
  const theadDataUrl = artifacts.theadCanvas.toDataURL('image/png');
  const metaDataUrl = metaOutput.dataUrl;

  const pages: PrintPageSlice[] = [];
  let srcY = 0;
  let pageIndex = 0;
  let metaPlaced = !metaDataUrl;

  while (srcY < artifacts.tbodyCanvas.height || (!metaPlaced && pageIndex === 0)) {
    let y = PRINT_PAGE_MARGIN_TOP_MM + PRINT_BODY_PADDING_TOP_MM;
    if (pageIndex === 0) {
      y += PRINT_BROWSER_HEADER_ALLOWANCE_MM;
    }

    const includeMeta = !metaPlaced && !!metaDataUrl;
    if (includeMeta) {
      y += pdfMetaTopGapMm(true) + metaHm + PDF_BLOCK_GAP_MM;
    }
    y += theadHm + PRINT_IMG_STACK_GAP_MM;

    const remainingMm = A4_HEIGHT_MM - PRINT_PAGE_MARGIN_OTHER_MM - y;
    if (remainingMm < MIN_TABLE_BODY_SLICE_MM) {
      if (includeMeta && srcY === 0) {
        pages.push({
          meta: metaDataUrl ?? undefined,
          thead: theadDataUrl,
        });
        metaPlaced = true;
        pageIndex += 1;
        continue;
      }
      throw new Error('인쇄 페이지 레이아웃 계산에 실패했습니다.');
    }

    const maxSlicePx = Math.min(
      artifacts.tbodyCanvas.height - srcY,
      Math.max(1, Math.floor((remainingMm / tbodyTotalHm) * artifacts.tbodyCanvas.height))
    );

    const sliceCanvas = sliceCanvasVertical(artifacts.tbodyCanvas, srcY, maxSlicePx);
    pages.push({
      meta: includeMeta ? metaDataUrl ?? undefined : undefined,
      thead: theadDataUrl,
      bodySlice: sliceCanvas.toDataURL('image/png'),
    });

    if (includeMeta) metaPlaced = true;
    srcY += maxSlicePx;
    pageIndex += 1;

    if (srcY < artifacts.tbodyCanvas.height && pageIndex > 500) {
      throw new Error('인쇄 페이지 분할 한도를 초과했습니다.');
    }
  }

  return pages;
}

async function renderPdfFromArtifacts(artifacts: TableExportArtifacts, fileName: string): Promise<void> {
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pdfH = pdf.internal.pageSize.getHeight();
  const pdfW = pdf.internal.pageSize.getWidth();
  const contentW = pdfW - PDF_MARGIN_MM * 2;

  const theadHm = canvasHeightMm(artifacts.theadCanvas, contentW);
  const metaOutput = prepareMetaForOutput(artifacts.metaCanvas, contentW);
  const metaHm = metaOutput.heightMm;
  const tbodyTotalHm = canvasHeightMm(artifacts.tbodyCanvas, contentW);
  const theadDataUrl = artifacts.theadCanvas.toDataURL('image/png');
  const metaDataUrl = metaOutput.dataUrl;

  let srcY = 0;
  let pageIndex = 0;

  while (srcY < artifacts.tbodyCanvas.height) {
    if (pageIndex > 0) pdf.addPage();
    let y = PDF_MARGIN_MM;

    if (pageIndex === 0 && metaDataUrl) {
      y += pdfMetaTopGapMm(true);
      pdf.addImage(metaDataUrl, 'PNG', PDF_MARGIN_MM, y, contentW, metaHm);
      y += metaHm + PDF_BLOCK_GAP_MM;
    }

    pdf.addImage(theadDataUrl, 'PNG', PDF_MARGIN_MM, y, contentW, theadHm);
    y += theadHm;

    const remainingMm = pdfH - PDF_MARGIN_MM - y;
    const maxSlicePx = Math.min(
      artifacts.tbodyCanvas.height - srcY,
      Math.max(1, Math.floor((remainingMm / tbodyTotalHm) * artifacts.tbodyCanvas.height))
    );

    const sliceCanvas = sliceCanvasVertical(artifacts.tbodyCanvas, srcY, maxSlicePx);
    const sliceHm = canvasHeightMm(sliceCanvas, contentW);
    pdf.addImage(sliceCanvas.toDataURL('image/png'), 'PNG', PDF_MARGIN_MM, y, contentW, sliceHm);

    srcY += maxSlicePx;
    pageIndex += 1;
  }

  pdf.save(fileName);
}

async function renderPrintFromArtifacts(artifacts: TableExportArtifacts, title: string): Promise<void> {
  const contentW = resolvePrintContentWidthMm();
  const pages = buildPrintPageSlices(artifacts, contentW);

  const pagesHtml = pages
    .map((page, index) => {
      const imgs = [
        page.meta ? `<img src="${page.meta}" alt="" />` : '',
        `<img src="${page.thead}" alt="" />`,
        page.bodySlice ? `<img src="${page.bodySlice}" alt="" />` : '',
      ]
        .filter(Boolean)
        .join('');
      const breakClass = index > 0 ? ' cp-modal-print-page-break' : '';
      return `<div class="cp-modal-print-page${breakClass}">${imgs}</div>`;
    })
    .join('');

  const printHtml = `
    <html>
      <head>
        <meta charset="utf-8" />
        <title>${BROWSER_PRINT_DOCUMENT_TITLE}</title>
        <style>
          ${buildPrintTablePageCss()}
        </style>
      </head>
      <body>
        ${pagesHtml}
      </body>
    </html>
  `;
  printHtmlDocument(printHtml);
}

/** 테이블: 메타 1회 + thead 1회 + tbody 연속. 페이지 넘김 시 thead 반복 (이미지 캡처) */
export async function saveModalTableContentAsPdf(params: {
  root: HTMLElement;
  fileName: string;
  metaHeaderSelector?: string;
  tableSelector?: string;
  captureCss?: string;
  useLiveComputedStyles?: boolean;
}): Promise<void> {
  const {
    root,
    fileName,
    metaHeaderSelector = '[data-pdf-element="header"]',
    tableSelector = 'table',
    captureCss = DEFAULT_CAPTURE_CSS,
    useLiveComputedStyles = true,
  } = params;

  const artifacts = await buildTableExportArtifacts(
    root,
    metaHeaderSelector,
    tableSelector,
    captureCss,
    useLiveComputedStyles
  );
  await renderPdfFromArtifacts(artifacts, fileName);
}

/** 블록(표 래퍼 등) 단일 캡처 후 세로 분할 — 다중 tbody·rowSpan 표에 적합 */
export async function saveModalBlockContentAsPdf(params: {
  root: HTMLElement;
  fileName: string;
  metaHeaderSelector?: string;
  bodySelector: string;
  captureCss?: string;
  useLiveComputedStyles?: boolean;
} & BlockContentExportHooks): Promise<void> {
  const {
    root,
    fileName,
    metaHeaderSelector = '[data-pdf-element="header"]',
    bodySelector,
    captureCss = DEFAULT_CAPTURE_CSS,
    useLiveComputedStyles = true,
    beforeCapture,
    prepareBodyClone,
    prepareTableSectionClone,
    customizeCaptureClone,
    createCaptureCloneCustomizer,
    disableSegmentedTableFallback,
    captureMinWidthPx,
    bodyInlineStyleOptions,
  } = params;

  const artifacts = await buildBlockExportArtifacts(
    root,
    metaHeaderSelector,
    bodySelector,
    captureCss,
    useLiveComputedStyles,
    {
      beforeCapture,
      prepareBodyClone,
      prepareTableSectionClone,
      customizeCaptureClone,
      createCaptureCloneCustomizer,
      disableSegmentedTableFallback,
      captureMinWidthPx,
      bodyInlineStyleOptions,
    }
  );
  await renderPdfFromBlockArtifacts(artifacts, fileName);
}

/** 블록 단일 캡처 A4 인쇄 */
export async function printModalBlockContentA4(params: {
  root: HTMLElement;
  title: string;
  metaHeaderSelector?: string;
  bodySelector: string;
  captureCss?: string;
  useLiveComputedStyles?: boolean;
} & BlockContentExportHooks): Promise<void> {
  const {
    root,
    title,
    metaHeaderSelector = '[data-pdf-element="header"]',
    bodySelector,
    captureCss = DEFAULT_CAPTURE_CSS,
    useLiveComputedStyles = true,
    beforeCapture,
    prepareBodyClone,
    prepareTableSectionClone,
    customizeCaptureClone,
    createCaptureCloneCustomizer,
    disableSegmentedTableFallback,
    captureMinWidthPx,
    bodyInlineStyleOptions,
  } = params;

  const artifacts = await buildBlockExportArtifacts(
    root,
    metaHeaderSelector,
    bodySelector,
    captureCss,
    useLiveComputedStyles,
    {
      beforeCapture,
      prepareBodyClone,
      prepareTableSectionClone,
      customizeCaptureClone,
      createCaptureCloneCustomizer,
      disableSegmentedTableFallback,
      captureMinWidthPx,
      bodyInlineStyleOptions,
    }
  );
  await renderPrintFromBlockArtifacts(artifacts, title);
}

export async function saveBlockArtifactsAsPdf(
  artifacts: BlockExportArtifacts,
  fileName: string,
  options?: BlockArtifactRenderOptions
): Promise<void> {
  await renderPdfFromBlockArtifacts(artifacts, fileName, options);
}

export async function printBlockArtifactsA4(
  artifacts: BlockExportArtifacts,
  title: string,
  options?: BlockArtifactRenderOptions
): Promise<void> {
  await renderPrintFromBlockArtifacts(artifacts, title, options);
}

/** A4 인쇄 — PDF와 동일한 이미지 캡처 페이지 구성 */
export async function printModalTableContentA4(params: {
  root: HTMLElement;
  title: string;
  metaHeaderSelector?: string;
  tableSelector?: string;
  captureCss?: string;
  useLiveComputedStyles?: boolean;
}): Promise<void> {
  const {
    root,
    title,
    metaHeaderSelector = '[data-pdf-element="header"]',
    tableSelector = 'table',
    captureCss = DEFAULT_CAPTURE_CSS,
    useLiveComputedStyles = true,
  } = params;

  const artifacts = await buildTableExportArtifacts(
    root,
    metaHeaderSelector,
    tableSelector,
    captureCss,
    useLiveComputedStyles
  );
  await renderPrintFromArtifacts(artifacts, title);
}
